import { configQuery, getFeatureEnabled, getTrackingService, RoutablePage, Throttle, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __awaiter, __generator, __assign, __decorate, __metadata } from 'tslib';
import { Prop, Emit, Component, Mixins } from 'vue-property-decorator';
import { UTM_PARAMS, Feature } from '@gig/magic-messages';
import { isUsernameStoreEnabled, SignupErrorTranslationKeys, SignUpError, storeUsername } from './sign-up.constants.js';
import { SIGN_UP_STEPS } from '@gig/magic-account-types';
import { SIGN_UP_NAMESPACE } from '../../store/modules/sign-up/sign-up.constants.js';
import '../../store/modules/sign-up/sign-up.store.js';
import { store } from '../../store/index.js';
import { namespace } from 'vuex-class';
import { ESubmitState, findComponentIdByServerId, FormBuilder } from '@gig/magic-form-builder';
import { setReverseMonth } from '../../utils/form-config.utils.js';

var signUpModule = namespace(SIGN_UP_NAMESPACE);
var SignUpV2Component = /** @class */ (function (_super) {
    __extends(SignUpV2Component, _super);
    function SignUpV2Component() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.submitData = {};
        _this.updateField = {};
        /**
         * Represent the state of the form.
         * Pre submit is the default value.
         */
        _this.submitState = ESubmitState.PRE_SUBMIT;
        /**
         * Which verification method to prefer when signing the user up.
         */
        _this.preferredVerificationMethods = {
            DEFAULT: ['SMS', 'EMAIL'],
        };
        return _this;
    }
    SignUpV2Component.prototype.signupError = function (error) {
        var errorData = this.getErrorMessage(error);
        if (errorData.isDynamic) {
            return this.$t(errorData.message, { ERROR_CODE: error });
        }
        else {
            return this.$t(errorData.message);
        }
    };
    SignUpV2Component.prototype.created = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this;
                        return [4 /*yield*/, getFeatureEnabled(Feature.MDY_FORMAT)];
                    case 1:
                        _a.isMdyFormatEnabled = _b.sent();
                        if (this.isMdyFormatEnabled && this.formConfig) {
                            this.applyMdyFormatToFormConfig();
                        }
                        if (!this.isStatelessStep) {
                            this.routeTo({
                                route: this.routePage,
                                params: { step: SIGN_UP_STEPS.STEP_1 },
                            });
                        }
                        configQuery('signup.preferredVerificationMethod', function (methods) {
                            if (methods) {
                                _this.preferredVerificationMethods = methods;
                            }
                        });
                        this.utmParamsSubscription = window.eventBus.subscribe(UTM_PARAMS, function (event) {
                            _this.utmParams = event.payload;
                        }, { replay: true });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Applies MDY format to date components in the form configuration
     * This method mutates the formConfig to enable reverse-month on date components
     */
    SignUpV2Component.prototype.applyMdyFormatToFormConfig = function () {
        var _this = this;
        if (!this.formConfig || !Array.isArray(this.formConfig)) {
            return;
        }
        this.formConfig.forEach(function (step, stepIndex) {
            if (!step.components || !Array.isArray(step.components)) {
                return;
            }
            step.components.forEach(function (component) {
                if (_this.isDateComponent(component)) {
                    try {
                        // Apply MDY format using the existing utility function
                        setReverseMonth(component);
                    }
                    catch (error) {
                        console.error("Failed to apply MDY format to component in step " + (stepIndex + 1) + ":", error);
                    }
                }
            });
        });
    };
    /**
     * Determines if a form component is a date component that needs MDY formatting
     * @param component - The form component to check
     * @returns true if the component is a date component
     */
    SignUpV2Component.prototype.isDateComponent = function (component) {
        var _this = this;
        if (component.id === 'date-field') {
            return true;
        }
        if (component.children && Array.isArray(component.children)) {
            return component.children.some(function (child) {
                return _this.isDateComponent(child);
            });
        }
        return false;
    };
    Object.defineProperty(SignUpV2Component.prototype, "isStatelessStep", {
        get: function () {
            /**
             * List of signup steps that don't need any existing form state (initial step and email verification).
             * In these cases we can access directly the step via url (or refresh the page), otherwise we should
             * redirect the user to initial step.
             */
            return [SIGN_UP_STEPS.STEP_1, this.verificationTokenStep].includes(this.currentStep);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SignUpV2Component.prototype, "verificationPromptStep", {
        get: function () {
            return (this.formConfig.length + 1).toString();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SignUpV2Component.prototype, "verificationTokenStep", {
        get: function () {
            return 'verification';
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Handler for each change event to the for builder state.
     * @param event
     * @returns
     */
    SignUpV2Component.prototype.onChange = function (event) {
        var form = event.detail[0].form;
        this.updateFormData(form);
    };
    SignUpV2Component.prototype.registerConfirmed = function (authModel) {
        getTrackingService().trackRegistrationConfirmed(this.utmParams, authModel.btag);
    };
    SignUpV2Component.prototype.onSubmit = function (event) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this.submitState === ESubmitState.SUBMITTING) {
                    return [2 /*return*/];
                }
                this.submitState = ESubmitState.SUBMITTING;
                this.submitData = event.detail[0];
                this.performSignup();
                this.signupAttempt(event.detail[0]);
                return [2 /*return*/];
            });
        });
    };
    SignUpV2Component.prototype.signupAttempt = function (authModel) {
        getTrackingService().trackRegistrationAttempt(authModel.email, this.utmParams, authModel.btag);
    };
    Object.defineProperty(SignUpV2Component.prototype, "routePage", {
        get: function () {
            return this.isLanding
                ? RoutablePage.LANDING_SIGNUP
                : RoutablePage.SIGN_UP;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(SignUpV2Component.prototype, "showRegistrationForm", {
        get: function () {
            var _this = this;
            return this.formConfig.some(function (step) { return step.path === _this.currentStep; });
        },
        enumerable: false,
        configurable: true
    });
    SignUpV2Component.prototype.performSignup = function () {
        return __awaiter(this, void 0, void 0, function () {
            var registrationType, error_1, typedError, formattedErrorCodes, errorCode;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.persistSignUpData();
                        return [4 /*yield*/, this.fetchVerificationType(this.preferredVerificationMethods)];
                    case 1:
                        registrationType = _a.sent();
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 5, , 6]);
                        return [4 /*yield*/, this.register()];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, this.storeUsername()];
                    case 4:
                        _a.sent();
                        this.resetFormModel();
                        this.nextStep({ registerType: registrationType });
                        this.submitState = ESubmitState.POST_SUBMIT_SUCCESS;
                        return [3 /*break*/, 6];
                    case 5:
                        error_1 = _a.sent();
                        this.submitState = ESubmitState.POST_SUBMIT_ERROR;
                        typedError = error_1;
                        if (typedError.status === 400 && typedError.errorCodes) {
                            formattedErrorCodes = '';
                            if (typedError.errorCodes.$) {
                                formattedErrorCodes = typedError.errorCodes.$.join(', ');
                            }
                            else {
                                formattedErrorCodes = Object.entries(typedError.errorCodes)
                                    .map(function (_a) {
                                    var field = _a[0], codes = _a[1];
                                    return field + ":" + codes
                                        .map(function (code) { return code.toLowerCase(); })
                                        .join(',');
                                })
                                    .join(', ');
                                this.checkForFieldValidationError(typedError);
                            }
                            errorCode = this.getFirstErrorCode(typedError.errorCodes);
                            this.signupError(errorCode || formattedErrorCodes);
                        }
                        else {
                            console.error(error_1);
                            this.signupError('UNEXPECTED_REGISTER_ERROR');
                        }
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    SignUpV2Component.prototype.persistSignUpData = function () {
        var allData = __assign(__assign({ urlReferrer: document.referrer }, this.submitData), this.additionalFields);
        this.updateSignupData(allData);
    };
    SignUpV2Component.prototype.storeUsername = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var data, username, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        data = this.submitData;
                        username = (data === null || data === void 0 ? void 0 : data.username) ||
                            ((_a = this.additionalFields.username) === null || _a === void 0 ? void 0 : _a.toString()) ||
                            (data === null || data === void 0 ? void 0 : data.email);
                        _b = username;
                        if (!_b) return [3 /*break*/, 2];
                        return [4 /*yield*/, isUsernameStoreEnabled()];
                    case 1:
                        _b = (_c.sent());
                        _c.label = 2;
                    case 2:
                        if (_b) {
                            storeUsername(username);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    SignUpV2Component.prototype.nextStep = function (params) {
        if (params === void 0) { params = {}; }
        return params;
    };
    SignUpV2Component.prototype.checkForFieldValidationError = function (typedError) {
        var _this = this;
        var apiErrors = typedError.errorCodes;
        if (!apiErrors) {
            return;
        }
        this.updateField = {};
        Object.keys(apiErrors).forEach(function (errorKey) {
            var components = _this.formConfig[0].components;
            var mappedField = findComponentIdByServerId(components, errorKey) || '';
            if (mappedField) {
                _this.updateField[mappedField] = {
                    id: mappedField,
                    error: _this.$t(SignupErrorTranslationKeys.MISMATCH_ERROR_TRANSLATION),
                    isValid: false,
                };
            }
        });
    };
    SignUpV2Component.prototype.getErrorMessage = function (error) {
        switch (error) {
            case SignUpError.MULTIPLE_ACCOUNT_CHECK_FAILED:
                return {
                    message: SignupErrorTranslationKeys.MULTIPLE_ACCOUNT_CHECK_FAILED,
                    isDynamic: true,
                };
            case SignUpError.USER_PROFILE_MISSING:
                return {
                    message: SignupErrorTranslationKeys.USER_PROFILE_MISSING,
                    isDynamic: true,
                };
            case SignUpError.ID_VERIFICATION_FAILED:
                return {
                    messsage: SignupErrorTranslationKeys.ID_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.AGE_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.AGE_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.ADVERSE_MEDIA_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.ADVERSE_MEDIA_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.ADDRESS_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.ADDRESS_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.SOURCE_OF_WEALTH_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.SOURCE_OF_WEALTH_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.KYC_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.KYC_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.SELF_EXCLUSION_CHECK_FAILED:
                return {
                    message: SignupErrorTranslationKeys.SELF_EXCLUSION_CHECK_FAILED,
                    isDynamic: true,
                };
            case SignUpError.PEP_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.PEP_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.SANCTION_VERIFICATION_FAILED:
                return {
                    message: SignupErrorTranslationKeys.SANCTION_VERIFICATION_FAILED,
                    isDynamic: true,
                };
            case SignUpError.PEP_MONITORING_FAILED:
                return {
                    message: SignupErrorTranslationKeys.PEP_MONITORING_FAILED,
                    isDynamic: true,
                };
            case SignUpError.SANCTION_MONITORING_FAILED:
                return {
                    message: SignupErrorTranslationKeys.SANCTION_MONITORING_FAILED,
                    isDynamic: true,
                };
            case SignUpError.JSON_PAYLOAD_INVALID:
                return {
                    message: SignupErrorTranslationKeys.JSON_PAYLOAD_INVALID,
                    isDynamic: true,
                };
            case SignUpError.MISMATCH:
                return {
                    message: SignupErrorTranslationKeys.MISMATCH,
                    isDynamic: false,
                };
            default:
                return {
                    message: SignupErrorTranslationKeys.GENERIC,
                    isDynamic: false,
                };
        }
    };
    SignUpV2Component.prototype.getFirstErrorCode = function (errors) {
        var errorKeys = Object.keys(errors);
        if (errorKeys.length > 0) {
            return errors[errorKeys[0]][0];
        }
        return 'MULTIPLE_ACCOUNT_CHECK';
    };
    SignUpV2Component.prototype.destroy = function () {
        var _a;
        (_a = this.utmParamsSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
    };
    __decorate([
        signUpModule.Action('updateSignupData'),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "updateSignupData", void 0);
    __decorate([
        signUpModule.Action('updateFormData'),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "updateFormData", void 0);
    __decorate([
        signUpModule.Action('register'),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "register", void 0);
    __decorate([
        signUpModule.Action('resetFormModel'),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "resetFormModel", void 0);
    __decorate([
        signUpModule.Getter('formModel'),
        __metadata("design:type", void 0)
    ], SignUpV2Component.prototype, "formModel", void 0);
    __decorate([
        signUpModule.Getter('authModel'),
        __metadata("design:type", void 0)
    ], SignUpV2Component.prototype, "authModel", void 0);
    __decorate([
        signUpModule.Action('fetchVerificationType'),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "fetchVerificationType", void 0);
    __decorate([
        Prop({
            type: Object,
            default: {},
        }),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "additionalFields", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], SignUpV2Component.prototype, "isLanding", void 0);
    __decorate([
        Prop(Array),
        __metadata("design:type", Array)
    ], SignUpV2Component.prototype, "formConfig", void 0);
    __decorate([
        Prop({ type: String, default: '1' }),
        __metadata("design:type", String)
    ], SignUpV2Component.prototype, "currentStep", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "userId", void 0);
    __decorate([
        Prop(String),
        __metadata("design:type", Object)
    ], SignUpV2Component.prototype, "verificationCode", void 0);
    __decorate([
        Emit('signup-error'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", void 0)
    ], SignUpV2Component.prototype, "signupError", null);
    __decorate([
        Throttle(500),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [CustomEvent]),
        __metadata("design:returntype", void 0)
    ], SignUpV2Component.prototype, "onChange", null);
    __decorate([
        Emit('next-step'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", void 0)
    ], SignUpV2Component.prototype, "nextStep", null);
    SignUpV2Component = __decorate([
        Component({
            name: 'corex-sign-up',
            store: store,
            components: {
                FormBuilder: FormBuilder,
            },
        })
    ], SignUpV2Component);
    return SignUpV2Component;
}(Mixins(MagicConfigMixin)));

export default SignUpV2Component;
